import React, { forwardRef, useId } from 'react';
import PropTypes from 'prop-types';
import styles from './Input.module.scss';

/* Ref forwarding goes to the inner <input>, so a consumer can
 * focus or select it (search hotkeys, focus-first-error on a
 * form). Purely additive — existing callers pass no ref and are
 * unaffected. Matches the pattern already used by IconButton.
 *
 * The field gets an id of its own when the caller passes none, so
 * the label is always tied to it, and the error / helper text is
 * announced with it via aria-describedby (QA CM-19: most callers
 * pass no id, which left their labels pointing at nothing). A
 * caller's own id, aria-invalid or aria-describedby still wins or
 * is kept. */

const Input = forwardRef(({
  label,
  error,
  helperText,
  leftIcon,
  rightIcon,
  id,
  className,
  ...rest
}, ref) => {
  const autoId = useId();
  const inputId = id || autoId;
  const messageId = error || helperText ? `${inputId}-msg` : undefined;
  const describedBy =
    [rest['aria-describedby'], messageId].filter(Boolean).join(' ') || undefined;
  const wrapperClasses = [styles.wrapper, className || ''].filter(Boolean).join(' ');
  const inputClasses = [
    styles.input,
    leftIcon ? styles.hasLeftIcon : '',
    rightIcon ? styles.hasRightIcon : '',
    error ? styles.inputError : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={wrapperClasses}>
      {label && (
        <label htmlFor={inputId} className={styles.label}>
          {label}
        </label>
      )}
      <div className={styles.inputWrap}>
        {leftIcon && <span className={styles.leftIcon}>{leftIcon}</span>}
        <input
          ref={ref}
          id={inputId}
          className={inputClasses}
          aria-invalid={error ? true : undefined}
          {...rest}
          aria-describedby={describedBy}
        />
        {rightIcon && <span className={styles.rightIcon}>{rightIcon}</span>}
      </div>
      {error && <span id={messageId} className={styles.error}>{error}</span>}
      {!error && helperText && (
        <span id={messageId} className={styles.helperText}>{helperText}</span>
      )}
    </div>
  );
});

Input.displayName = 'Input';

Input.propTypes = {
  label: PropTypes.string,
  error: PropTypes.string,
  helperText: PropTypes.string,
  leftIcon: PropTypes.node,
  rightIcon: PropTypes.node,
  id: PropTypes.string,
  className: PropTypes.string,
};

Input.defaultProps = {
  label: '',
  error: '',
  helperText: '',
  leftIcon: null,
  rightIcon: null,
  id: undefined,
  className: '',
};

export default Input;
